import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Snowflake } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { api } from '../services/api'
import { FreezeActiveModal } from './modals/FreezeActiveModal'

/**
 * Глобальный индикатор активной заморозки — рендерится поверх любого экрана.
 * • Пульсирующая снежинка с обратным отсчётом «N ч».
 * • Тап → FreezeActiveModal с таймером и пояснением.
 * • Поп-ап автоматически показывается только ОДИН раз за сессию (sessionStorage flag).
 * • Сам чип остаётся видимым, пока заморозка активна.
 */
const SESSION_KEY = 'tfa.freezeModalShown'

export function FreezeStatusChip() {
  const { data: streakStatus } = useQuery({
    queryKey: ['streak', 'status'],
    queryFn: () => api.getStreakStatus(),
    refetchInterval: 60_000,
  })

  const freezeActive = !!streakStatus?.freezeActive
  const hours = streakStatus?.hoursRemaining ?? null

  const [modalOpen, setModalOpen] = useState(false)

  // При появлении активной заморозки — открыть модалку один раз за сессию.
  useEffect(() => {
    if (!freezeActive) return
    if (typeof window === 'undefined') return
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === '1') return
      window.sessionStorage.setItem(SESSION_KEY, '1')
    } catch { /* ignore */ }
    setModalOpen(true)
  }, [freezeActive])

  // Когда заморозка снимается — сбрасываем флаг, чтобы при следующей активации
  // поп-ап снова показался.
  useEffect(() => {
    if (freezeActive) return
    try { window.sessionStorage.removeItem(SESSION_KEY) } catch { /* ignore */ }
  }, [freezeActive])

  if (!freezeActive) return null

  return (
    <>
      <AnimatePresence>
        <motion.button
          key="freeze-chip"
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.6, opacity: 0 }}
          data-testid="global-freeze-chip"
          onClick={() => setModalOpen(true)}
          aria-label="Заморозка активна"
          style={{
            position: 'fixed',
            // Над TabBar (z-index 300), но ниже модалок (410+).
            zIndex: 320,
            // Прижат к правому краю над TabBar.
            right: 14,
            bottom: 'calc(112px + env(safe-area-inset-bottom, 0px))',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 12px 8px 10px',
            borderRadius: 22,
            background: 'linear-gradient(135deg, #4A9FE5 0%, #6C5CE7 100%)',
            border: '1.5px solid rgba(255,255,255,0.55)',
            color: 'white',
            cursor: 'pointer',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 13, fontWeight: 800,
            boxShadow: '0 8px 22px rgba(74,159,229,0.45), 0 0 0 2px rgba(74,159,229,0.25)',
          }}
          className="freeze-chip-pulse"
        >
          <Snowflake size={16} color="white" />
          {hours != null && hours > 0 ? `${Math.ceil(hours)} ч` : 'Заморозка'}
          <style>{`
            @keyframes freeze-chip-pulse {
              0%, 100% { box-shadow: 0 8px 22px rgba(74,159,229,0.45), 0 0 0 2px rgba(74,159,229,0.25); transform: scale(1); }
              50%      { box-shadow: 0 8px 28px rgba(74,159,229,0.75), 0 0 0 6px rgba(74,159,229,0.30); transform: scale(1.06); }
            }
            @keyframes freeze-snowflake-spin {
              from { transform: rotate(0deg); }
              to   { transform: rotate(360deg); }
            }
            .freeze-chip-pulse {
              animation: freeze-chip-pulse 1.8s ease-in-out infinite;
            }
            .freeze-chip-pulse svg {
              animation: freeze-snowflake-spin 6s linear infinite;
              transform-origin: center;
            }
          `}</style>
        </motion.button>
      </AnimatePresence>

      <FreezeActiveModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialSeconds={hours != null ? Math.max(0, Math.round(hours * 3600)) : undefined}
        streakDays={streakStatus?.streakDays ?? 0}
      />
    </>
  )
}
